// src/components/Prescriptions/index.jsx
import React, { useState, useEffect } from "react";
import { Table, Avatar, Button, Tag, Popconfirm, Card, App } from "antd";
import { EyeOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import AddPrescriptionModal from "./AddPrescriptionModal";
import PrescriptionDetailsModal from "./PrescriptionDetailModal";
import {
  apiDeletePrescription,
  apiGetPrescriptions,
} from "../../api/prescription";
import "./Prescription.css";

const Prescriptions = () => {
  const { message } = App.useApp();
  const [activeTab, setActiveTab] = useState("all");
  const [loading, setLoading] = useState(false);
  const [prescriptions, setPrescriptions] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [editPrescriptionId, setEditPrescriptionId] = useState(null);
  const [pharmacy, setPharmacy] = useState(null);

  useEffect(() => {
    fetchPrescriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, pagination.current]);

  const fetchPrescriptions = async () => {
    try {
      setLoading(true);

      const params = {
        page: pagination.current,
        per_page: pagination.pageSize,
      };

      // your API uses numeric statuses; adapt if needed
      if (activeTab === "drafts") params.status = 1;
      if (activeTab === "sent") params.status = 4; // show "sent to pharmacy" in Sent tab

      const response = await apiGetPrescriptions(params);
      const data = response?.data || [];
      const meta = response?.meta || {};

      // if pharmacy returned at top-level
      setPharmacy(response?.pharmacy || null);

      // map response items to the fields UI expects
      const mapped = (Array.isArray(data) ? data : []).map((p) => ({
        ...p,
        id: p.id,
        name: p.name,
        profileImage: p.profileImage || null,
        medicationName: p.medicationName,
        medicationDosage: p.medicationDosage,
        date: p.date || null, // adapt to actual API
        // crucial flags:
        status: Number(p.status),
        isPremium: Boolean(p.isPremium),
      }));

      setPrescriptions(mapped);
      setPagination((prev) => ({
        ...prev,
        current: meta.currentPage || prev.current,
        total: (meta.totalPages || 1) * prev.pageSize,
      }));
    } catch (error) {
      console.error("Error fetching prescriptions:", error);
      message.error("Failed to load prescriptions");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (prescriptionId) => {
    try {
      await apiDeletePrescription(prescriptionId);
      message.success("Prescription deleted successfully");
      fetchPrescriptions();
    } catch (error) {
      console.error("Error deleting prescription:", error);
      message.error("Failed to delete prescription");
    }
  };

  const handleViewDetails = (record) => {
    setSelectedPrescription(record);
    setDetailsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditPrescriptionId(record.id);
    setAddModalVisible(true);
  };

  const getStatusTag = (status) => {
    // Map based on your six-case description:
    // 1 = Draft, 2 = Approved (or Sent to patient), 3 = Paid, 4 = Sent to pharmacy
    const map = {
      1: { color: "blue", text: "Draft" },
      2: { color: "green", text: "Approved" }, // or "Sent to patient"
      3: { color: "lightgreen", text: "Paid" },
      4: { color: "darkblue", text: "Sent to Pharmacy" },
    };
    const s = map[status] || { color: "default", text: "Unknown" };
    return (
      <Tag color={s.color} className="rounded-full px-3 py-1">
        {s.text}
      </Tag>
    );
  };

  const columns = [
    {
      title: "Patient",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div className="flex items-center gap-3">
          <Avatar
            size={40}
            src={record.profileImage}
            className="bg-purple-100 text-purple-600"
          >
            {!record.profileImage && text?.charAt(0)}
          </Avatar>
          <span className="font-small text-gray-900">{text}</span>
        </div>
      ),
    },
    {
      title: "Medication",
      dataIndex: "medicationName",
      key: "medicationName",
      render: (text) => <span className="text-gray-700">{text}</span>,
    },
    {
      title: "Dosage",
      dataIndex: "medicationDosage",
      key: "medicationDosage",
      render: (text) => <span className="text-gray-700">{text}</span>,
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (timestamp) => (
        <span className="text-gray-700">
          {timestamp ? new Date(timestamp * 1000).toLocaleDateString() : "-"}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => getStatusTag(status),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const { status, isPremium } = record;

        // Determine which actions to show / enable per your 6 cases:
        // case1: status=1 & !isPremium => show Send to Patient button (row) and edit available
        // case2: status=2 & !isPremium => show Send to Pharmacy button (row)
        // case3: status=3 & !isPremium => edit shows both send to patient & pharmacy in modal (not row)
        // case4: status=4 & !isPremium => only view; edit/delete disabled
        // case5: status=1 & isPremium => editable but only send to pharmacy in edit modal (row show edit)
        // case6: status=4 & isPremium => only view; edit/delete disabled

        const isDraft = status === 1;
        const disableEditOrDelete = status === 4; // case4 & case6
        const showSendToPharmacyRow = status === 2 && !isPremium;

        return (
          <div className="flex items-center gap-2">
            <Button
              type="text"
              icon={<EyeOutlined />}
              className="text-gray-600 hover:text-purple-600"
              onClick={() => handleViewDetails(record)}
            />
            <Button
              type="text"
              icon={<EditOutlined />}
              className={`text-gray-600 hover:text-purple-600 ${
                disableEditOrDelete ? "opacity-40 cursor-not-allowed" : ""
              }`}
              onClick={() => !disableEditOrDelete && handleEdit(record)}
              disabled={disableEditOrDelete || showSendToPharmacyRow}
            />
            <Popconfirm
              title="Delete prescription?"
              description="Are you sure you want to delete this prescription?"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ danger: true }}
            >
              <Button
                type="text"
                icon={<DeleteOutlined />}
                className={`text-gray-600 hover:text-red-600 ${
                  disableEditOrDelete ? "opacity-40 cursor-not-allowed" : ""
                }`}
                disabled={disableEditOrDelete || !isDraft}
              />
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  const filteredData = prescriptions.filter((item) => {
    if (activeTab === "all") return true;
    if (activeTab === "drafts") return item.status === 1;
    if (activeTab === "sent") return item.status === 4;
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Prescription Management
          </h1>
          <p className="text-gray-500 mt-1">
            Create, manage and send digital prescriptions to pharmacies
          </p>
        </div>
        <Button
          type="primary"
          icon={
            <img
              src="/icon/add_prescription_icon.svg"
              alt="Add"
              className="h-5 w-5"
            />
          }
          size="large"
          className="bg-purple-600 hover:bg-purple-700 border-purple-600"
          onClick={() => {
            setEditPrescriptionId(null);
            setAddModalVisible(true);
          }}
        >
          Add Prescription
        </Button>
      </div>

      <Card
        title="Prescriptions"
        className="shadow-sm rounded-lg"
        extra={
          <div className="flex gap-4">
            <Button onClick={() => setActiveTab("all")}>All</Button>
            <Button onClick={() => setActiveTab("drafts")}>Draft</Button>
            <Button onClick={() => setActiveTab("sent")}>Sent</Button>
          </div>
        }
      >
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: false,
            onChange: (page) =>
              setPagination((prev) => ({ ...prev, current: page })),
          }}
          className="prescription-table"
        />
      </Card>

      <AddPrescriptionModal
        visible={addModalVisible}
        prescriptionId={editPrescriptionId}
        onClose={() => {
          setAddModalVisible(false);
          setEditPrescriptionId(null);
          // The modal will reset discountInput internally
        }}
        onSuccess={() => {
          fetchPrescriptions();
        }}
        pharmacy={pharmacy}
      />

      <PrescriptionDetailsModal
        visible={detailsModalVisible}
        prescription={selectedPrescription}
        onClose={() => {
          setDetailsModalVisible(false);
          setSelectedPrescription(null);
        }}
      />
    </div>
  );
};

export default Prescriptions;
